/**
 * The police cars' light bars and sirens.
 *
 * A light bar is a dark bar across the roof with a lamp at each end, drawn
 * for every police car in two instanced meshes: the bars, and the lamps,
 * whose instance colour is how lit each is. Responding, the lamps flash
 * blue, left and right in turn, a few times a second. At night one blue
 * light follows the nearest responding car, so it lights the street round
 * it; it is in the scene from the start, dark, because adding a light
 * later recompiles every lit material.
 *
 * The siren is synthesised, as the horn is (`horn.ts`): a sawtooth, through
 * a low-pass and a little clipping, its pitch following the country's
 * siren (`sirenPitch`), shifted by how fast the car is closing on the
 * listener, and fainter with distance. The nearest two responding cars are
 * heard.
 */

import type { Object3D } from 'three';
import { BoxGeometry, Color, DynamicDrawUsage, Group, InstancedMesh, Matrix4, Mesh, MeshBasicMaterial, MeshStandardMaterial, PointLight, Vector3 } from 'three';
import { LIVERY, LIVERY_HEADER, LIVERY_PAINT } from './flora.js';
import { addStreetLampObjectShader } from './terrainMaterial.js';
import type { VehicleModel } from './Vehicle.js';
import { isBus, isTruck, roofTop } from './Vehicle.js';
import type { SirenKind } from '../core/traffic/police.js';
import { doppler, sirenPitch } from '../core/traffic/police.js';
import type { SoundSystem } from './audio/system.js';

/** How often the lamps flash, a second, left and right in turn. */
const FLASH_HZ = 2.6;
const LIT = new Color(0.25, 0.45, 4.0);
const DARK = new Color(0.08, 0.1, 0.22);
/** The bar's size, metres: across, high, along. */
const BAR = [0.95, 0.09, 0.24] as const;
const barMaterial = new MeshStandardMaterial({ color: 0x2a2d33, roughness: 0.6, flatShading: true });

const bar = new Matrix4();
const lamp = new Matrix4();
const at = new Vector3();

export class PoliceLights {
  readonly group = new Group();
  private readonly bars: InstancedMesh;
  private readonly lamps: InstancedMesh;
  /** The one blue light, for the nearest responding car at night. */
  readonly light = new PointLight(0x3060ff, 0, 22, 1.6);
  private count = 0;
  private nearest = Infinity;

  constructor(capacity: number) {
    this.bars = new InstancedMesh(new BoxGeometry(BAR[0], BAR[1], BAR[2]).translate(0, BAR[1] / 2, 0),
      barMaterial, capacity);
    this.lamps = new InstancedMesh(new BoxGeometry(0.38, BAR[1] + 0.02, BAR[2] + 0.02).translate(0, BAR[1] / 2, 0),
      new MeshBasicMaterial({ color: 0xffffff, toneMapped: false }), capacity * 2);
    for (const mesh of [this.bars, this.lamps]) {
      mesh.instanceMatrix.setUsage(DynamicDrawUsage);
      mesh.frustumCulled = false;
      mesh.count = 0;
      this.group.add(mesh);
    }
    this.lamps.setColorAt(0, DARK);
    this.lamps.instanceColor!.setUsage(DynamicDrawUsage);
    this.bars.castShadow = true;
    this.group.add(this.light);
  }

  /** Start a frame's placing. */
  begin(): void {
    this.count = 0;
    this.nearest = Infinity;
    this.light.intensity = 0;
  }

  /**
   * A police car drawn at `body` (its world matrix), its roof top `roofY`
   * high at `roofZ` along it. Responding, its lamps flash, by `clock`
   * seconds and a phase of its own; `night` (0..1) and how far it is from
   * the camera say whether it gets the blue light.
   */
  place(body: Matrix4, roofY: number, roofZ: number, responding: boolean, clock: number, phase: number, distance: number, night: number): void {
    const i = this.count++;
    bar.makeTranslation(0, roofY, roofZ).premultiply(body);
    this.bars.setMatrixAt(i, bar);
    const beat = Math.floor((clock + phase) * FLASH_HZ * 2);
    for (const side of [0, 1]) {
      lamp.makeTranslation(side === 0 ? -0.28 : 0.28, roofY, roofZ).premultiply(body);
      this.lamps.setMatrixAt(i * 2 + side, lamp);
      // Left, then right, a short gap between: each lamp lit a third of the time.
      const lit = responding && beat % 3 === side;
      this.lamps.setColorAt(i * 2 + side, lit ? LIT : DARK);
    }
    if (responding && night > 0.2 && distance < this.nearest) {
      this.nearest = distance;
      at.set(0, roofY + 0.4, roofZ).applyMatrix4(body);
      this.light.position.copy(at);
      // It pulses as the lamps do.
      this.light.intensity = (beat % 3 === 2 ? 0.4 : 3) * night;
    }
  }

  /** Done placing for the frame. */
  commit(): void {
    this.bars.count = this.count;
    this.lamps.count = this.count * 2;
    this.bars.instanceMatrix.needsUpdate = true;
    this.lamps.instanceMatrix.needsUpdate = true;
    this.lamps.instanceColor!.needsUpdate = true;
  }

  /** Where it is in the scene. */
  addTo(parent: Object3D): void {
    parent.add(this.group);
  }
}

/** A responding car as a listener hears it: which, how it sounds, how far, and how fast it is closing. */
export interface SirenSource {
  id: number;
  kind: SirenKind;
  distance: number;
  closing: number;
}

/** Heard out to this far, metres. */
const HEARD = 450;
/** How many at once. */
const VOICES = 2;

interface Voice {
  osc: OscillatorNode;
  gain: GainNode;
  /** When it started, the context's clock: where in its cycle it is. */
  start: number;
}

export class Sirens {
  private readonly voices = new Map<number, Voice>();

  constructor(private readonly sound: SoundSystem) {}

  /** Sound the nearest of `sources`, and stop any no longer among them. */
  update(sources: readonly SirenSource[]): void {
    const heard = !this.sound.live ? [] : sources.filter(s => s.distance < HEARD).sort((a, b) => a.distance - b.distance).slice(0, VOICES);
    const ctx = this.sound.context, out = this.sound.input('world');
    if ((heard.length === 0 && this.voices.size === 0) || ctx === undefined || out === undefined) return;
    const now = ctx.currentTime;
    const keep = new Set(heard.map(s => s.id));
    for (const [id, voice] of this.voices) {
      if (keep.has(id)) continue;
      voice.gain.gain.setTargetAtTime(0, now, 0.08);
      voice.osc.stop(now + 0.5);
      this.voices.delete(id);
    }
    for (const s of heard) {
      let voice = this.voices.get(s.id);
      if (voice === undefined) voice = this.startVoice(ctx, out, s.id);
      const pitch = sirenPitch(s.kind, now - voice.start) * doppler(s.closing);
      voice.osc.frequency.setTargetAtTime(pitch, now, s.kind === 'twoTone' ? 0.015 : 0.05);
      const level = 0.11 * Math.max(0, 1 - s.distance / HEARD) ** 2;
      voice.gain.gain.setTargetAtTime(level, now, 0.05);
    }
  }

  private startVoice(ctx: AudioContext, out: AudioNode, id: number): Voice {
    const osc = ctx.createOscillator();
    osc.type = 'sawtooth';
    const tone = ctx.createBiquadFilter();
    tone.type = 'lowpass';
    tone.frequency.value = 2400;
    tone.Q.value = 1.2;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    osc.connect(tone).connect(gain).connect(out);
    // Each one starts its cycle somewhere of its own.
    const start = ctx.currentTime - Math.random() * 4;
    osc.start();
    const voice = { osc, gain, start };
    this.voices.set(id, voice);
    return voice;
  }
}

// --- a police car to drive -------------------------------------------------------

/** A drivable police car's own light bar: its two lamps, to flash. */
export interface PoliceKit {
  bar: Group;
  lamps: [Mesh, Mesh];
}

/**
 * Make a drivable car (`createVehicle`) a police car: the country's livery
 * (`LIVERY`) painted over its body colour, as the traffic's are, but
 * measured in the body's own frame, since its panels are parts of their own
 * placed on it; and a light bar on its roof. Only a saloon or an estate is
 * laid out for it.
 */
export function makePolice(model: VehicleModel): PoliceKit {
  const paint = model.paint;
  const toBody = new Matrix4();
  paint.onBeforeCompile = (shader) => {
    addStreetLampObjectShader(shader);
    Object.assign(shader.uniforms, LIVERY, { uToBody: { value: toBody } });
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nuniform mat4 uToBody;\nvarying vec3 vLiveryAt;\nvarying vec3 vLiveryFacing;\nvarying float vLiveryTint;')
      .replace('#include <begin_vertex>', `#include <begin_vertex>
#ifdef USE_INSTANCING
  mat4 liveryModel = modelMatrix * instanceMatrix;
#else
  mat4 liveryModel = modelMatrix;
#endif
  vLiveryAt = (uToBody * liveryModel * vec4(transformed, 1.0)).xyz;
  vLiveryFacing = mat3(uToBody) * mat3(liveryModel) * objectNormal;
  vLiveryTint = 1.0;`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>\n${LIVERY_HEADER}`)
      .replace('#include <color_fragment>', `#include <color_fragment>\n${LIVERY_PAINT}`);
  };
  paint.customProgramCacheKey = () => 'police-livery';
  paint.color.setHex(0xffffff);
  paint.needsUpdate = true;
  // Each painted panel says where the body is, as it is drawn.
  model.body.traverse((o) => {
    if (o instanceof Mesh && o.material === paint) o.onBeforeRender = () => { toBody.copy(model.body.matrixWorld).invert(); };
  });

  const roof = isTruck(model.kind) ? { y: 2.6, z: 0 } : isBus(model.kind) ? { y: 3.05, z: 0 } : roofTop(model.kind);
  const bar = new Group();
  bar.position.set(0, roof.y, roof.z);
  const base = new Mesh(new BoxGeometry(BAR[0], BAR[1], BAR[2]).translate(0, BAR[1] / 2, 0), barMaterial);
  base.castShadow = true;
  const lamp = (x: number) => {
    const mesh = new Mesh(new BoxGeometry(0.38, BAR[1] + 0.02, BAR[2] + 0.02).translate(x, BAR[1] / 2, 0), new MeshBasicMaterial({ color: DARK, toneMapped: false }));
    bar.add(mesh);
    return mesh;
  };
  bar.add(base);
  const lamps: [Mesh, Mesh] = [lamp(-0.28), lamp(0.28)];
  model.body.add(bar);
  return { bar, lamps };
}

/** Flash a drivable police car's lamps, `on` or dark, by `clock` seconds. */
export function flashKit(kit: PoliceKit, on: boolean, clock: number): void {
  const beat = Math.floor(clock * FLASH_HZ * 2);
  kit.lamps.forEach((lamp, side) => (lamp.material as MeshBasicMaterial).color.copy(on && beat % 3 === side ? LIT : DARK));
}
